/**
 * NewProjectModal — 새 프로젝트 생성 모달
 */

import React, { useState } from 'react';
import { X, Globe, Clock, Languages, Sparkles } from 'lucide-react';
import { clsx } from 'clsx';

interface NewProjectModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    url: string;
    name: string;
    preferences: {
      video_length: string;
      language: string;
      tone: string;
      include_narration: boolean;
      include_bgm: boolean;
      resolution: string;
    };
  }) => Promise<void>;
}

const VIDEO_LENGTHS = [
  { value: '30s', label: '30초', desc: 'SNS 광고용' },
  { value: '60s', label: '1분', desc: '랜딩페이지 추천' },
  { value: '90s', label: '1분 30초', desc: '상세 기능 소개' },
  { value: '120s', label: '2분', desc: '풀 데모' },
];

const LANGUAGES = [
  { value: 'ko', label: '한국어', flag: '🇰🇷' },
  { value: 'en', label: 'English', flag: '🇺🇸' },
  { value: 'ja', label: '日本語', flag: '🇯🇵' },
];

const TONES = [
  { value: 'professional', label: '프로페셔널' },
  { value: 'friendly', label: '프렌들리' },
  { value: 'playful', label: '플레이풀' },
  { value: 'minimal', label: '미니멀' },
];

export function NewProjectModal({ open, onClose, onSubmit }: NewProjectModalProps) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [videoLength, setVideoLength] = useState('60s');
  const [language, setLanguage] = useState('ko');
  const [tone, setTone] = useState('professional');
  const [includeNarration, setIncludeNarration] = useState(true);
  const [includeBgm, setIncludeBgm] = useState(true);

  if (!open) return null;

  const handleSubmit = async () => {
    setLoading(true);
    const normalizedUrl = url.match(/^https?:\/\//) ? url : `https://${url}`;
    try {
      await onSubmit({
        url: normalizedUrl,
        name: name || new URL(normalizedUrl).hostname,
        preferences: {
          video_length: videoLength,
          language,
          tone,
          include_narration: includeNarration,
          include_bgm: includeBgm,
          resolution: '1080p',
        },
      });
      onClose();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" />
            <h2 className="text-lg font-semibold">새 데모 영상 만들기</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6">
          {/* Step 1: URL 입력 */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  웹서비스 URL
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://your-service.com"
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  프로젝트 이름 (선택)
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="자동으로 도메인명 사용"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-300"
                />
              </div>

              <button
                onClick={() => setStep(2)}
                disabled={!url}
                className={clsx(
                  'w-full py-3 rounded-xl font-medium transition',
                  url
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                )}
              >
                다음: 영상 옵션 설정
              </button>
            </div>
          )}

          {/* Step 2: 옵션 설정 */}
          {step === 2 && (
            <div className="space-y-5">
              {/* 영상 길이 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" /> 영상 길이
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {VIDEO_LENGTHS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setVideoLength(opt.value)}
                      className={clsx(
                        'p-2 rounded-lg border text-center transition',
                        videoLength === opt.value
                          ? 'border-purple-400 bg-purple-50 text-purple-700'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <div className="font-semibold text-sm">{opt.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* 언어 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Languages className="w-4 h-4 inline mr-1" /> 언어
                </label>
                <div className="flex gap-2">
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.value}
                      onClick={() => setLanguage(lang.value)}
                      className={clsx(
                        'flex-1 py-2 px-3 rounded-lg border text-sm transition',
                        language === lang.value
                          ? 'border-purple-400 bg-purple-50 text-purple-700'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      {lang.flag} {lang.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 톤 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">톤앤매너</label>
                <div className="grid grid-cols-4 gap-2">
                  {TONES.map(t => (
                    <button
                      key={t.value}
                      onClick={() => setTone(t.value)}
                      className={clsx(
                        'py-2 px-3 rounded-lg border text-sm transition',
                        tone === t.value
                          ? 'border-purple-400 bg-purple-50 text-purple-700'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 토글 옵션 */}
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeNarration}
                    onChange={(e) => setIncludeNarration(e.target.checked)}
                    className="w-4 h-4 rounded text-purple-600 focus:ring-purple-300"
                  />
                  <span className="text-sm text-gray-700">내레이션 포함</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeBgm}
                    onChange={(e) => setIncludeBgm(e.target.checked)}
                    className="w-4 h-4 rounded text-purple-600 focus:ring-purple-300"
                  />
                  <span className="text-sm text-gray-700">배경음악 포함</span>
                </label>
              </div>

              {/* Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-3 border border-gray-200 rounded-xl text-sm hover:bg-gray-50 transition"
                >
                  이전
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      생성 중...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      데모 영상 생성 시작
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
